import { useState, useEffect, useCallback } from 'react';
import { getUserOrganizations } from '@/lib/firebase/queries/organizations/getUserOrganizations';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { Organization } from '@/types/organization';
import { keepOrPickOrganization } from '@/lib/manager/pickOrganization';

// The manager's last organization, per browser. Storage can be unavailable
// (private mode, blocked site data), so every access is guarded.
const rememberKey = (uid: string) => `namat:managerOrg:${uid}`;
function readRememberedOrg(uid: string): string | null {
  try { return localStorage.getItem(rememberKey(uid)); } catch { return null; }
}
function rememberOrg(uid: string, orgId: string) {
  try { localStorage.setItem(rememberKey(uid), orgId); } catch { /* not persisted */ }
}

export function useManagerOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const fetchManagerOrganizations = useCallback(async () => {
    if (!currentUser) {
      setOrganizations([]);
      setSelectedOrganization(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Fetching manager organizations for user:', currentUser.uid);
      
      // Get all organizations where user is a manager
      const managerOrgs = await getUserOrganizations(currentUser.uid, 'manager');
      console.log('👔 Found', managerOrgs.length, 'manager organizations');
      
      setOrganizations(managerOrgs);
      setSelectedOrganization((current) => keepOrPickOrganization(managerOrgs, current, readRememberedOrg(currentUser.uid)));
      
    } catch (error) {
      console.error('Error fetching manager organizations:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const switchOrganization = useCallback((organization: Organization) => {
    console.log('🔄 Switching to organization:', organization.name);
    setSelectedOrganization(organization);
    if (currentUser) rememberOrg(currentUser.uid, organization.id);
    toast.success(`Switched to ${organization.name}`);
  }, [currentUser]);

  useEffect(() => {
    fetchManagerOrganizations();
  }, [fetchManagerOrganizations]);

  return {
    organizations,
    selectedOrganization,
    loading,
    switchOrganization,
    refreshOrganizations: fetchManagerOrganizations,
    hasMultipleOrganizations: organizations.length > 1
  };
}